// START IMPORTS
import {
  PayPalRedirectionModal,
  PaymentsSelectionModal,
  RadioPaymentsModal,
  ViewOrderHistory,
} from '@ltvco/refresh-lib/payments';
import { Grid, Box } from '@ltvco/refresh-lib/theme';
import {
  AccountInformation,
  GenericCtaCard,
  LimitedPlanProgress,
  MyAccountHeader,
} from '@ltvco/refresh-lib/v1';
import { AccountEditModals } from '@ltvco/refresh-lib/v2';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';

import {
  AccountInformationOptions,
  GenericCtaCardProps,
  editOptionClicked,
} from './BillingConstants';
// END IMPORTS

export function Billing({
  onCancelConfirmCancelModal,
}: {
  onCancelConfirmCancelModal?: () => void;
}) {
  // START HOOKS_AND_QUERIES
  const navigate = useNavigate();
  const {
    isLimitedUser,
    isUnlimitedUser,
    reportLimit,
    reportsRun,
    reportsRunPercentage,
    isLoading,
  } = useLimitedPlanInfo();
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [openPaymentsSelectionModal, setOpenPaymentsSelectionModal] =
    useState<boolean>(false);

  const [openPaymentsUpdateModal, setOpenPaymentsUpdateModal] =
    useState<boolean>(false);
  const [openPaypalRedirection, setOpenPaypalRedirection] =
    useState<boolean>(false);
  const [paypalState, setPaypalState] = useState<'success' | 'canceled'>(
    'success'
  );
  const [editing, setEditing] = useState<string | null>('');
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const { ctaCardProps, length } = GenericCtaCardProps(navigate);
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    document.title = 'My Account - NumberGuru';
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('bvppcanc')) {
      setPaypalState('canceled');
      setOpenPaypalRedirection(true);
      return;
    }
    if (urlParams.get('bvpp') && urlParams.get('token')) {
      setPaypalState('success');
      setOpenPaypalRedirection(true);
    }
  }, []);
  // END EFFECTS

  // START EVENT_HANDLERS
  const togglePaymentsSelectionModal = () =>
    setOpenPaymentsSelectionModal(!openPaymentsSelectionModal);

  const togglePaymentsUpdateModal = () => {
    setOpenPaymentsUpdateModal(!openPaymentsUpdateModal);
    if (!openPaymentsSelectionModal) {
      togglePaymentsSelectionModal();
    }
  };

  const togglePaypalRedirectionModal = () =>
    setOpenPaypalRedirection(!openPaypalRedirection);

  const finishEditing = () => {
    setEditing('');
  };
  // END EVENT_HANDLERS

  // START RENDER

  return (
    <>
      <MyAccountHeader isUnlimited={isUnlimitedUser} />
      {isLimitedUser && !isLoading && (
        <LimitedPlanProgress
          progressValue={reportsRunPercentage}
          reportsLimit={reportLimit}
          reportsRun={reportsRun}
          link="/upgrade/plan"
        />
      )}
      <Grid container spacing={2} justifyContent="center">
        {[...Array(length)].map((_x, index) => (
          <Grid
            size={{
              sm: 12,
              md: 6,
            }}
            key={index}
          >
            <GenericCtaCard {...ctaCardProps[index]}></GenericCtaCard>
          </Grid>
        ))}
      </Grid>

      <AccountEditModals
        editing={editing}
        finishEditing={finishEditing}
        onCancelConfirmCancelModal={onCancelConfirmCancelModal}
        showCancellationReasonsOnCancel
      />
      <Box mt={2.5} nonce={undefined}>
        <AccountInformation
          editOptions={AccountInformationOptions}
          editOptionClick={(value: string | null) => {
            editOptionClicked(value, setOpenPaymentsSelectionModal, setEditing);
          }}
        />
      </Box>
      <ViewOrderHistory />
      <PaymentsSelectionModal
        open={openPaymentsSelectionModal}
        onClose={togglePaymentsSelectionModal}
        onAddPaymentMethod={() => {
          setOpenPaymentsSelectionModal(false);
          setOpenPaymentsUpdateModal(true);
        }}
      />
      <RadioPaymentsModal
        isOpen={openPaymentsUpdateModal}
        onCloseHandle={togglePaymentsUpdateModal}
      />
      <PayPalRedirectionModal
        paypalState={paypalState}
        isOpen={openPaypalRedirection}
        onCloseHandle={togglePaypalRedirectionModal}
      />
    </>
  );
  // END RENDER
}
